// /floor-plan — Floor Plan Editor (PAGES.md §3.27)
//
// Visual drag-and-drop editor for restaurant floor layout. Position tables on
// a grid/canvas, set sections/zones, and define table shapes.
// RESTAURANT vertical only — MANAGER+ (level 30).

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Select,
  Input,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  Alert,
  ConfirmDialog,
  Modal,
  useToast,
  FloorPlan,
} from '@bbpos/ui';
import type { TablePosition } from '@bbpos/ui/src/components/RestaurantComponents/FloorPlan/types';
import { tableService } from '@/services/table.service';
import { floorLayoutService } from '@/services/floor-layout.service';
import type { DiningTable, TableShape } from '@/services/table.service';
import { buildCreateTablePayload } from './floor-plan-payload';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FILTER, GLASS_FORM, cn } from '@/constants/design-tokens';
import { ApiError } from '@/lib/api-client';

// ─── Icons ──────────────────────────────────────────────────────────────────

function SaveIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function UndoIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function LayoutIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h18v18H3V3zm0 6h18M9 9v12" />
    </svg>
  );
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface PositionMap {
  [tableId: string]: { x: number; y: number; width: number; height: number };
}

interface UndoEntry {
  tableId: string;
  prev: { x: number; y: number };
  next: { x: number; y: number };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function getDefaultPosition(index: number): { x: number; y: number; width: number; height: number } {
  const cols = 6;
  const col = index % cols;
  const row = Math.floor(index / cols);
  return { x: 30 + col * 140, y: 30 + row * 130, width: 110, height: 100 };
}

// ─── Component ──────────────────────────────────────────────────────────────

function FloorPlanEditorContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const storeLocationId = useDashboardStore((s) => s.selectedLocationId);
  const profileLocations = useAuthStore((s) => s.profile?.locations);
  const selectedLocationId =
    storeLocationId ?? profileLocations?.find((l) => l.is_active)?.id ?? undefined;

  // ── State ───────────────────────────────────────────────────────────────
  const [tables, setTables] = useState<DiningTable[]>([]);
  const [positions, setPositions] = useState<PositionMap>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Undo stack
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);

  // Section filter
  const [sectionFilter, setSectionFilter] = useState<string>('');

  // Add table modal
  const [addTableOpen, setAddTableOpen] = useState(false);
  const [newTableNumber, setNewTableNumber] = useState('');
  const [newTableCapacity, setNewTableCapacity] = useState(2);
  const [newTableShape, setNewTableShape] = useState<TableShape>('rectangle');
  const [newTableFloor, setNewTableFloor] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Add section modal
  const [addSectionOpen, setAddSectionOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');

  // Refs for stable keyboard handler callbacks
  const handleSaveRef = useRef<() => void>(() => {});
  const handleUndoRef = useRef<() => void>(() => {});

  // Tracks whether we've already auto-selected a default section for the
  // current location, so the user's "clear" choice isn't overwritten on
  // every re-render.
  const sectionDefaultedRef = useRef(false);

  // ── Mobile detection ────────────────────────────────────────────────────
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ── Fetch tables + layout ─────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!selectedLocationId) return;
    setIsLoading(true);
    setError(null);
    try {
      // Paginate through all tables. API caps `limit` at 100 (PaginationPipe),
      // so a single request misses tables when a location has >100 of them.
      const PAGE_SIZE = 100;
      const [firstPage, layout] = await Promise.all([
        tableService.list({ location_id: selectedLocationId, page: 1, limit: PAGE_SIZE }),
        floorLayoutService.get(selectedLocationId),
      ]);
      const totalPages = firstPage.meta?.totalPages ?? 1;
      const allTables: DiningTable[] = [...firstPage.data];
      if (totalPages > 1) {
        const restPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, idx) =>
            tableService.list({ location_id: selectedLocationId, page: idx + 2, limit: PAGE_SIZE }),
          ),
        );
        restPages.forEach((p) => allTables.push(...p.data));
      }
      const tableResult = { ...firstPage, data: allTables };
      setTables(tableResult.data);

      // Merge existing layout positions with defaults for unplaced tables
      const layoutPositions: PositionMap = {};
      (layout?.layout?.tables ?? []).forEach((t) => {
        layoutPositions[t.table_id] = { x: t.x, y: t.y, width: t.width, height: t.height };
      });
      const mergedPositions: PositionMap = {};
      tableResult.data.forEach((t, i) => {
        if (layoutPositions[t.id]) {
          mergedPositions[t.id] = layoutPositions[t.id];
        } else {
          const def = getDefaultPosition(i);
          mergedPositions[t.id] = {
            x: t.position_x ?? def.x,
            y: t.position_y ?? def.y,
            width: def.width,
            height: def.height,
          };
        }
      });
      setPositions(mergedPositions);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load floor plan data.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedLocationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset the "default section" flag whenever the location changes — each
  // location has its own sections and deserves its own first-section default.
  useEffect(() => {
    sectionDefaultedRef.current = false;
    setSectionFilter('');
  }, [selectedLocationId]);

  // Default to the first available section after tables load, so the canvas
  // doesn't dump every table from every section on screen at once.
  useEffect(() => {
    if (sectionDefaultedRef.current) return;
    if (isLoading) return;
    if (tables.length === 0) return;
    const firstSection = tables
      .map((t) => t.section ?? t.floor ?? '')
      .find((s) => s);
    if (firstSection) {
      setSectionFilter(firstSection);
    }
    sectionDefaultedRef.current = true;
  }, [isLoading, tables]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) return;
      if (addTableOpen || addSectionOpen || resetConfirmOpen) return;

      // Ctrl+S — Save layout
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveRef.current();
        return;
      }
      // Ctrl+Z — Undo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndoRef.current();
        return;
      }
      // Delete — Remove selected from layout
      if (e.key === 'Delete' && selectedTableId) {
        e.preventDefault();
        setPositions((prev) => {
          const next = { ...prev };
          delete next[selectedTableId];
          return next;
        });
        setSelectedTableId(null);
        setHasUnsavedChanges(true);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTableId, addTableOpen, addSectionOpen, resetConfirmOpen]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleTableMove = useCallback(
    (tableId: string, newPos: Pick<TablePosition, 'x' | 'y'>) => {
      setPositions((prev) => {
        const oldPos = prev[tableId];
        if (oldPos) {
          setUndoStack((stack) => [
            ...stack,
            { tableId, prev: { x: oldPos.x, y: oldPos.y }, next: { x: newPos.x, y: newPos.y } },
          ]);
        }
        return {
          ...prev,
          [tableId]: { ...(prev[tableId] ?? { width: 110, height: 100 }), x: newPos.x, y: newPos.y },
        };
      });
      setHasUnsavedChanges(true);
    },
    [],
  );

  const handleTableClick = useCallback((tableId: string) => {
    setSelectedTableId((prev) => (prev === tableId ? null : tableId));
  }, []);

  const handleUndo = useCallback(() => {
    setUndoStack((stack) => {
      if (stack.length === 0) return stack;
      const last = stack[stack.length - 1];
      setPositions((prev) => ({
        ...prev,
        [last.tableId]: { ...(prev[last.tableId] ?? { width: 110, height: 100 }), x: last.prev.x, y: last.prev.y },
      }));
      setHasUnsavedChanges(true);
      return stack.slice(0, -1);
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!selectedLocationId) return;
    setIsSaving(true);
    try {
      await floorLayoutService.save(selectedLocationId, positions);
      setHasUnsavedChanges(false);
      toast({ title: 'Floor plan saved', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to save layout', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [selectedLocationId, positions, toast]);

  // ── Keep refs current for keyboard handler ──────────────────────────────
  useEffect(() => { handleSaveRef.current = handleSave; }, [handleSave]);
  useEffect(() => { handleUndoRef.current = handleUndo; }, [handleUndo]);

  // Unsaved-changes guard: warn before the tab/window is closed or refreshed
  // while there are dragged-but-unsaved table positions, so the layout isn't
  // discarded silently. Mirrors the Settings page guard (#273 / #282).
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleReset = useCallback(() => {
    const resetPositions: PositionMap = {};
    tables.forEach((t, i) => {
      const def = getDefaultPosition(i);
      resetPositions[t.id] = { x: def.x, y: def.y, width: def.width, height: def.height };
    });
    setPositions(resetPositions);
    setUndoStack([]);
    setHasUnsavedChanges(true);
    setResetConfirmOpen(false);
  }, [tables]);

  const handleAddTable = useCallback(async () => {
    if (!newTableNumber.trim() || !selectedLocationId) return;
    setIsCreating(true);
    try {
      const payload = buildCreateTablePayload({
        tableNumber: newTableNumber,
        seatingCapacity: newTableCapacity,
        shape: newTableShape,
        floor: newTableFloor,
        locationId: selectedLocationId,
      });
      await tableService.create(payload);
      setAddTableOpen(false);
      setNewTableNumber('');
      setNewTableCapacity(2);
      setNewTableFloor('');
      toast({ title: `Table ${newTableNumber} created`, variant: 'success' });
      await fetchData();
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to create table', variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  }, [newTableNumber, newTableCapacity, newTableShape, newTableFloor, selectedLocationId, fetchData, toast]);

  const handleAddSection = useCallback(() => {
    const trimmed = newSectionName.trim();
    if (!trimmed) return;
    // A section isn't a standalone record — it lives only as the floor/section
    // label on a table. Firing a "created" toast here persisted nothing, so the
    // section vanished on reload (issue #281). Instead, carry the name into the
    // Add Table modal: the section becomes real, persists across reloads, and
    // shows up in the section dropdown as soon as its first table is saved.
    setSectionFilter(trimmed);
    setNewTableFloor(trimmed);
    setNewSectionName('');
    setAddSectionOpen(false);
    setAddTableOpen(true);
  }, [newSectionName]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const sections = [...new Set(tables.map((t) => t.section ?? t.floor ?? '').filter(Boolean))];
  const sectionOptions = sections.map((s) => ({ value: s, label: s }));

  const filteredTables = sectionFilter
    ? tables.filter((t) => (t.section ?? t.floor ?? '') === sectionFilter)
    : tables;

  const selectedTable = tables.find((t) => t.id === selectedTableId) ?? null;

  // Tables placed on canvas
  const placedTableIds = new Set(Object.keys(positions));
  const unplacedTables = filteredTables.filter((t) => !placedTableIds.has(t.id));

  // Floor plan data for <FloorPlan>
  const floorPlanTables = filteredTables
    .filter((t) => positions[t.id])
    .map((t) => {
      const pos = positions[t.id] ?? { x: 0, y: 0, width: 110, height: 100 };
      const shape = t.shape ?? 'rectangle';
      // Circle/square need a square bounding box to render correctly.
      // Use the smaller dimension so the shape stays inside the saved bounds.
      const side = Math.min(pos.width, pos.height);
      const renderPos =
        shape === 'rectangle' ? pos : { ...pos, width: side, height: side };
      return {
        id: t.id,
        name: t.table_number,
        capacity: t.seating_capacity ?? 2,
        status: 'available' as const,
        shape,
        position: { tableId: t.id, ...renderPos },
      };
    });

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <PageContainer
      title="Floor Plan Editor"
      subtitle={hasUnsavedChanges ? 'Unsaved changes' : undefined}
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Floor Plan Editor' },
      ]}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleUndo}
            disabled={undoStack.length === 0}
            aria-label="Undo"
          >
            <UndoIcon />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setResetConfirmOpen(true)}
            className="text-danger-600 hover:text-danger-700"
            aria-label="Reset layout"
          >
            <ResetIcon />
            <span className="ml-1 hidden sm:inline">Reset</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAddSectionOpen(true)}
          >
            <PlusIcon />
            <span className="ml-1.5 hidden sm:inline">Add Section</span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // Pre-fill with the active section so the new table lands in the
              // section the user is currently working in (and shows up in the
              // section dropdown). Empty when no section is selected.
              setNewTableFloor(sectionFilter);
              setAddTableOpen(true);
            }}
          >
            <PlusIcon />
            <span className="ml-1.5">Add Table</span>
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            loading={isSaving}
            disabled={!hasUnsavedChanges}
          >
            <SaveIcon />
            <span className="ml-1.5">Save Layout</span>
          </Button>
        </div>
      }
    >
      {/* Mobile warning */}
      {isMobile && (
        <Alert
          variant="warning"
          description="Floor plan editor works best on tablet or desktop."
          className="mb-4"
        />
      )}

      {/* ═══ Toolbar ═══ */}
      {/*
        Toolbar must out-stack the canvas below — the canvas uses GLASS, whose
        `backdrop-filter` creates its own stacking context. Without an explicit
        z-index above it, the Section dropdown (absolute, z-50) gets occluded
        by the canvas/table cards rendered underneath. See issue #5.
      */}
      <div className={cn(GLASS_FILTER, 'relative z-30 flex flex-wrap items-center gap-3 p-3 mb-4 overflow-visible')}>
        {sections.length > 0 && (
          <div className="w-44">
            <Select
              options={sectionOptions}
              value={sectionFilter}
              onChange={(val) => setSectionFilter(typeof val === 'string' ? val : val[0] ?? '')}
              placeholder="Select section"
              clearable
            />
          </div>
        )}
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-xs text-neutral-500 dark:text-neutral-400 mr-2">
            {filteredTables.length} table{filteredTables.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* ═══ Error ═══ */}
      {error && (
        <Alert variant="danger" description={error} dismissible onDismiss={() => setError(null)} className="mb-4" />
      )}

      {/* ═══ Canvas + Sidebar ═══ */}
      {isLoading ? (
        <div className={cn(GLASS, 'p-6')}>
          <Skeleton variant="rect" width="100%" height={480} />
        </div>
      ) : (
        <div className="relative z-0 flex gap-4">
          {/* Canvas */}
          <div className={cn(GLASS, 'relative z-0 flex-1 overflow-hidden p-2')}>
            {filteredTables.length === 0 ? (
              <div className="flex items-center justify-center min-h-[480px]">
                <EmptyState
                  variant="inline"
                  icon={<LayoutIcon className="h-14 w-14" />}
                  title="No tables to arrange"
                  description="Create tables first, then drag them here to build your floor plan"
                  action={{
                    label: 'Add Table',
                    onClick: () => {
                      setNewTableFloor(sectionFilter);
                      setAddTableOpen(true);
                    },
                  }}
                />
              </div>
            ) : isMobile ? (
              /* Mobile: simple list view of positions */
              <div className="space-y-2 p-4">
                {filteredTables.map((t) => {
                  const pos = positions[t.id];
                  return (
                    <div
                      key={t.id}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg transition-colors',
                        selectedTableId === t.id
                          ? 'bg-violet-50 dark:bg-violet-900/20'
                          : 'bg-neutral-50 dark:bg-neutral-800/60 hover:bg-neutral-100 dark:hover:bg-neutral-700/40',
                      )}
                      onClick={() => setSelectedTableId(t.id)}
                      role="button"
                      aria-label={`Table ${t.table_number}`}
                    >
                      <span className="font-bold text-neutral-900 dark:text-white min-w-[60px]">{t.table_number}</span>
                      <span className="text-xs text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">
                        x: {pos?.x ?? 0}, y: {pos?.y ?? 0}
                      </span>
                      <Badge variant="info" size="sm" className="ml-auto">{t.seating_capacity ?? 2} seats</Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Desktop: drag-and-drop floor plan */
              <FloorPlan
                tables={floorPlanTables}
                onTableClick={handleTableClick}
                onTableMove={handleTableMove}
                editable
                className="min-h-[480px]"
              />
            )}
          </div>

          {/* Right sidebar — table properties + unplaced list */}
          <div className="w-72 shrink-0 hidden lg:flex flex-col gap-4">
            {/* Selected table properties */}
            {selectedTable ? (
              <div className={cn(GLASS_FORM, 'p-4 space-y-3')}>
                <h3 className="text-sm font-bold text-neutral-900 dark:text-white">
                  Table {selectedTable.table_number}
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500 dark:text-neutral-400">Capacity</span>
                    <span className="font-mono tabular-nums text-neutral-900 dark:text-white">
                      {selectedTable.seating_capacity ?? '—'} seats
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500 dark:text-neutral-400">Section</span>
                    <span className="text-neutral-900 dark:text-white">
                      {selectedTable.section ?? selectedTable.floor ?? '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500 dark:text-neutral-400">Shape</span>
                    <Badge variant="info" size="sm">{selectedTable.shape ?? 'rectangle'}</Badge>
                  </div>
                  {positions[selectedTable.id] && (
                    <div className="flex justify-between">
                      <span className="text-neutral-500 dark:text-neutral-400">Position</span>
                      <span className="font-mono tabular-nums text-xs text-neutral-600 dark:text-neutral-300">
                        ({Math.round(positions[selectedTable.id].x)}, {Math.round(positions[selectedTable.id].y)})
                      </span>
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-violet-600 dark:text-violet-400"
                  onClick={() => navigate(`/tables`)}
                >
                  Edit in Table Management →
                </Button>
              </div>
            ) : (
              <div className={cn(GLASS_FORM, 'p-4 text-center')}>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Click a table on the canvas to see its properties
                </p>
              </div>
            )}

            {/* Unplaced tables */}
            {unplacedTables.length > 0 && (
              <div className={cn(GLASS_FORM, 'p-4')}>
                <h3 className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-3">
                  Unplaced Tables ({unplacedTables.length})
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {unplacedTables.map((t, i) => (
                    <button
                      key={t.id}
                      type="button"
                      className="w-full text-left p-2 rounded-lg bg-neutral-50 dark:bg-neutral-800/60 hover:bg-violet-50 dark:hover:bg-violet-900/10 transition-colors text-sm"
                      onClick={() => {
                        const def = getDefaultPosition(Object.keys(positions).length + i);
                        setPositions((prev) => ({
                          ...prev,
                          [t.id]: { x: def.x, y: def.y, width: def.width, height: def.height },
                        }));
                        setHasUnsavedChanges(true);
                      }}
                      aria-label={`Place table ${t.table_number}`}
                    >
                      <span className="font-bold text-neutral-900 dark:text-white">{t.table_number}</span>
                      <span className="text-neutral-400 ml-2 text-xs">{t.seating_capacity ?? 2} seats</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ Unsaved changes indicator ═══ */}
      {hasUnsavedChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-violet-600 text-white shadow-xl">
            <span className="text-sm font-medium">Unsaved changes</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSave}
              loading={isSaving}
              className="text-white hover:bg-white/10"
            >
              Save Now
            </Button>
          </div>
        </div>
      )}

      {/* ═══ Reset Confirmation ═══ */}
      <ConfirmDialog
        open={resetConfirmOpen}
        onCancel={() => setResetConfirmOpen(false)}
        onConfirm={handleReset}
        title="Reset Floor Plan?"
        description="All table positions will be reset to default. This cannot be undone."
        confirmLabel="Reset"
        variant="danger"
      />

      {/* ═══ Add Table Modal ═══ */}
      <Modal
        open={addTableOpen}
        onClose={() => setAddTableOpen(false)}
        title="Add Table"
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setAddTableOpen(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddTable}
              loading={isCreating}
              disabled={!newTableNumber.trim()}
            >
              Create Table
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Table Number <span className="text-danger-500">*</span>
            </label>
            <Input
              value={newTableNumber}
              onChange={(val) => setNewTableNumber(val)}
              placeholder="e.g. T1, A5"
              aria-label="Table number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Seating Capacity
            </label>
            <Input
              type="number"
              value={String(newTableCapacity)}
              onChange={(val) => setNewTableCapacity(parseInt(val, 10) || 2)}
              min={1}
              max={50}
              aria-label="Seating capacity"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Floor / Section
            </label>
            <Input
              value={newTableFloor}
              onChange={(val) => setNewTableFloor(val)}
              placeholder="e.g. Ground, Rooftop, Outdoor, VIP"
              aria-label="Floor or section"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              Tables grouped by this value can be filtered using the section dropdown.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
              Shape
            </label>
            <Select
              options={[
                { value: 'rectangle', label: 'Rectangle' },
                { value: 'circle', label: 'Round' },
                { value: 'square', label: 'Square' },
              ]}
              value={newTableShape}
              onChange={(val) => setNewTableShape((typeof val === 'string' ? val : val[0]) as TableShape)}
              aria-label="Table shape"
            />
          </div>
        </div>
      </Modal>
      {/* ═══ Add Section Modal ═══ */}
      <Modal
        open={addSectionOpen}
        onClose={() => setAddSectionOpen(false)}
        title="Add Section"
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setAddSectionOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddSection}
              disabled={!newSectionName.trim()}
            >
              Add First Table
            </Button>
          </div>
        }
      >
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-200 mb-1">
            Section Name <span className="text-danger-500">*</span>
          </label>
          <Input
            value={newSectionName}
            onChange={(val) => setNewSectionName(val)}
            placeholder="e.g. Outdoor, VIP Lounge, Garden"
            aria-label="Section name"
          />
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">
            A section becomes available once it has at least one table. Name it here, then add
            its first table — the section is saved with that table and appears in the section dropdown.
          </p>
        </div>
      </Modal>
    </PageContainer>
  );
}

// ─── Export with ErrorBoundary ───────────────────────────────────────────────

export function FloorPlanEditorPage() {
  return (
    <ErrorBoundary>
      <FloorPlanEditorContent />
    </ErrorBoundary>
  );
}
